/**
 * Category Tours Page
 * Displays all tours for a specific category
 */

import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import SEO from '../components/Common/SEO';
import { categoryService, Category } from '../services/categoryService';
import { tourService } from '../services/tourService';
import { Tour } from '../types';
import { useTheme } from '../contexts/ThemeContext';
import TourCard from '../components/Tours/TourCard';
import { applyFeaturedOrderWithCategory } from '../utils/applyFeaturedOrder';
import { getCategoryTitle, getCategoryDescription } from '../utils/categoryTitles';

const CategoryPackages: React.FC = () => {
  const { categoryId } = useParams<{ categoryId: string }>();
  const navigate = useNavigate();
  const { actualTheme } = useTheme();
  const [category, setCategory] = useState<Category | null>(null);
  const [tours, setTours] = useState<Tour[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

  // Auto-advance slideshow - must be before any conditional returns
  useEffect(() => {
    // ENFORCE SYSTEM RULE: The first image of the first tour in a category is the category's cover image.
    // The slideshow for a category should be based on the images of that first (featured) tour.
    const firstTour = tours.length > 0 ? tours[0] : null;
    const coverImages = firstTour?.images || [];

    if (coverImages.length > 1) {
      const timer = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % coverImages.length);
      }, 5000);
      return () => clearInterval(timer);
    } else {
      setCurrentSlide(0);
    }
  }, [tours]);

  useEffect(() => {
    if (categoryId) {
      loadCategoryAndTours();
    }
  }, [categoryId]);

  // Listen for SWR background updates — fires when newly-added tours
  // (not in the static CDN snapshot) arrive from the live backend,
  // OR when a tour's category is reassigned (dispatched by TourForm on save).
  useEffect(() => {
    const handleCategoryUpdate = (e: Event) => {
      const event = e as CustomEvent;
      if (!event.detail) return;
      const { categoryId: updatedCatId, tours: freshTours } = event.detail;

      // Only update if this event is for the category we're currently viewing
      if (updatedCatId !== category?.id && updatedCatId !== categoryId) return;

      if (freshTours && freshTours.length > 0) {
        // We received a full fresh list — sort and render immediately
        const sorted = applyFeaturedOrderWithCategory(freshTours, updatedCatId);
        setTours(sorted);
      } else {
        // Empty list means "please re-fetch" (e.g. after a category reassignment)
        loadCategoryAndTours();
      }
    };

    window.addEventListener('categoryToursUpdated', handleCategoryUpdate);
    // Also respond to the broad toursUpdated signal so a save always re-renders
    window.addEventListener('toursUpdated', loadCategoryAndTours);
    return () => {
      window.removeEventListener('categoryToursUpdated', handleCategoryUpdate);
      window.removeEventListener('toursUpdated', loadCategoryAndTours);
    };
  }, [category?.id, categoryId]);

  const loadCategoryAndTours = async () => {
    try {
      setLoading(true);

      if (categoryId) {
        let categoryData = await categoryService.getCategoryById(categoryId);

        if (!categoryData) {
          const allCategories = await categoryService.getAllCategories();
          categoryData = allCategories.find(cat =>
            cat.id.toLowerCase() === categoryId.toLowerCase() ||
            cat.name.toLowerCase() === categoryId.toLowerCase()
          ) || null;
        }

        if (!categoryData) {
          setLoading(false);
          navigate('/categories', { replace: true });
          return;
        }

        setCategory(categoryData);
        let categoryTours = await tourService.getToursByCategoryId(categoryData.id);
        
        categoryTours = applyFeaturedOrderWithCategory(categoryTours, categoryData.id);
        setTours(categoryTours);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };


  if (loading) {
    return (
      <div className={`min-h-screen ${actualTheme === 'dark' ? 'bg-slate-900' : 'bg-white'} flex items-center justify-center`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-800 mx-auto"></div>
          <p className="mt-4 text-slate-600 dark:text-slate-400">Loading packages...</p>
        </div>
      </div>
    );
  }

  if (!category) {
    return (
      <div className={`min-h-screen ${actualTheme === 'dark' ? 'bg-slate-900' : 'bg-white'} flex items-center justify-center`}>
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-800 dark:text-white mb-4">Category not found</h1>
          <Link to="/" className="text-primary-800 hover:text-primary-700">Return to homepage</Link>
        </div>
      </div>
    );
  }

  // ENFORCE SYSTEM RULE: The first image of the first tour in a category is the category's cover image.
  // The slideshow for a category should be based on the images of that first (featured) tour.
  const firstTour = tours.length > 0 ? tours[0] : null;
  const coverImages = firstTour?.images || [];

  return (
    <div className={`min-h-screen ${actualTheme === 'dark' ? 'bg-slate-900' : 'bg-neutral-50'}`}>
      <SEO
        title={`${category?.name || 'Category'} Tours`}
        description={category?.description || `Explore our collection of ${category?.name} tours and packages.`}
        image={tours?.[0]?.images?.[0]}
        keywords={[category?.name || '', 'safari', 'tours', 'kenya', 'africa']}
      />
      {/* Floating Hero Section with Rounded Edges */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 sm:pt-12">
        <div className="relative h-64 md:h-80 lg:h-96 overflow-hidden rounded-3xl shadow-2xl border-4 border-white/20 dark:border-slate-700/30 backdrop-blur-sm">
          {/* Background Images Slideshow */}
          {coverImages.length > 0 ? (
            <div className="absolute inset-0">
              {coverImages.map((image, index) => (
                <div
                  key={index}
                  className={`absolute inset-0 transition-opacity duration-1000 ${index === currentSlide ? 'opacity-100' : 'opacity-0'
                    }`}
                >
                  <img
                    src={image}
                    alt={`${category?.name} tour ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/40 to-black/70"></div>
                </div>
              ))}
            </div>
          ) : (
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{
                backgroundImage: `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url(https://images.unsplash.com/photo-1516026672322-bc52d61a55d5?ixlib=rb-4.0.3&auto=format&fit=crop&w=2072&q=80)`
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/40 to-black/70"></div>
            </div>
          )}

          {/* Content Overlay */}
          <div className="relative z-10 h-full flex items-center justify-center">
            <div className="text-center text-white px-4">
              <div className="mb-6">
                <span
                  className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium text-white shadow-md ${actualTheme === 'dark' ? 'bg-primary-700' : 'bg-primary-800'}`}
                >
                  {category?.name}
                </span>
              </div>
              <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold mb-4 line-clamp-2 md:line-clamp-none leading-tight">{category?.name} Tours</h1>
              <p className="text-sm sm:text-lg md:text-xl max-w-2xl mx-auto line-clamp-3 md:line-clamp-none">{category?.description}</p>
            </div>
          </div>

        </div>
      </div>

      {/* Category Description Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className={`rounded-2xl p-6 md:p-8 shadow-lg ${actualTheme === 'dark'
          ? 'bg-slate-800/50 border border-slate-700/50'
          : 'bg-white border border-neutral-200/50'
          }`}>
          <div className="max-w-4xl mx-auto">
            <h2 className={`text-2xl md:text-3xl font-bold mb-4 ${actualTheme === 'dark' ? 'text-slate-200' : 'text-primary-900'
              }`}>
              {getCategoryTitle(category?.name || '')}
            </h2>
            <div className={`text-base md:text-lg leading-relaxed ${actualTheme === 'dark' ? 'text-slate-300' : 'text-neutral-700'
              }`}>
              {(() => {
                const fullDescription = category?.description || getCategoryDescription(category?.name || '');

                const words = fullDescription.split(' ');
                const truncatedWords = words.slice(0, 20);
                const truncatedDescription = truncatedWords.join(' ') + (words.length > 20 ? '...' : '');

                return (
                  <>
                    <p className="mb-3">
                      {isDescriptionExpanded ? fullDescription : truncatedDescription}
                    </p>
                    {words.length > 20 && (
                      <button
                        onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
                        className={`text-sm font-medium transition-colors duration-200 ${actualTheme === 'dark'
                          ? 'text-primary-400 hover:text-primary-300'
                          : 'text-primary-800 hover:text-primary-700'
                          }`}
                      >
                        {isDescriptionExpanded ? 'Read less' : 'Read more'}
                      </button>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      </div>

      {/* Tours Grid */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 pb-12 sm:pb-16 md:pb-20">
        <div className="mb-4 sm:mb-6 md:mb-8">
          <h2 className={`text-xl sm:text-2xl md:text-3xl font-bold ${actualTheme === 'dark' ? 'text-slate-200' : 'text-primary-900'
            }`}>
            Available Tours
          </h2>
          <p className={`mt-1 sm:mt-2 text-xs sm:text-sm md:text-base ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-600'
            }`}>
            {tours.length} {tours.length === 1 ? 'tour' : 'tours'} available in this category
          </p>
        </div>

        {loading ? (
          <div className="flex flex-col sm:flex-row justify-center items-center py-12 sm:py-20 gap-4">
            <div className="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-b-2 border-primary-800"></div>
            <span className="text-base sm:text-xl text-neutral-600">Loading tours...</span>
          </div>
        ) : (
          <>
            {/* Tours Grid */}
            {tours.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
                {tours.map((tour) => (
                  <TourCard
                    key={tour.id}
                    tour={tour}
                    showQuickBook={true}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className={`text-lg ${actualTheme === 'dark' ? 'text-slate-400' : 'text-neutral-500'
                  }`}>
                  No tours found for this category.
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CategoryPackages;

